import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { TrackingContext } from '@vrbo/react-event-tracking';
import PropTypes from 'prop-types';
import { ASYNC_FUNC, pageStates } from './PageConfig';
import { collapseQueryParams } from '../../utils/StringUtil';
import AppActions from '../../actions/app';
import ExperimentUtil from '../../utils/ExperimentUtil';
import {
    isClAddressImprovements,
    isGeocoderEnabled,
    isUnitCheckEnabled
} from '../../utils/ExperimentsUtil';
import { langIdOnLocale } from '../../utils/LocaleUtil/languageList';

export const PageFlow = WrappedComponent => (class extends Component {
    displayNameToUse = '';

    // eslint-disable-next-line react/sort-comp
    static contextType = TrackingContext;

    static propTypes = {
        displayName: PropTypes.string,
        setDestination: PropTypes.func.isRequired,
        rootPath: PropTypes.string.isRequired,
        history: PropTypes.shape({
            push: PropTypes.func.isRequired
        }).isRequired,
        locale: PropTypes.shape({
            display: PropTypes.string.isRequired,
            canonical: PropTypes.string.isRequired
        }).isRequired,
        setDisableUFO: PropTypes.func.isRequired,
        eligibilityStatus: PropTypes.string.isRequired
    }

    static defaultProps = {
        displayName: undefined // We fall back to the class name if this is undefined
    }

    constructor(props) {
        super(props);

        this.getDisplayName = this.getDisplayName.bind(this);
        this.getCurrentPageState = this.getCurrentPageState.bind(this);
        this.getNextPageName = this.getNextPageName.bind(this);
        this.browseToNextPage = this.browseToNextPage.bind(this);
        this.setState = this.setState.bind(this);
        this.next = this.next.bind(this);
        this.nextPageLink = this.nextPageLink.bind(this);
        this.getLink = this.getLink.bind(this);
        this.setDisplayNameToUse = this.setDisplayNameToUse.bind(this);
    }

    setDisplayNameToUse(displayName) {
        this.displayNameToUse = displayName;
    }

    getDisplayName() {
        const { displayName } = this.props;
        return displayName || WrappedComponent.displayName || WrappedComponent.name;
    }

    getCurrentPageState() {
        const pageName = this.displayNameToUse || this.getDisplayName();
        const currentPageState = pageStates.find(pageState => pageState.name === pageName);
        if (!currentPageState) {
            throw new Error('Failed to get the current page state!');
        }
        return currentPageState;
    }

    getNextPageName() {
        const currentPageState = this.getCurrentPageState();
        const {
            setDestination
        } = this.props;
        // eslint-disable-next-line no-restricted-syntax
        for (const page of currentPageState.nextPages) {
            const { predicate, pageName, destination } = page;
            const result = this.evaluatePredicate(predicate);
            if (result) {
                if (destination) {
                    setDestination(destination);
                }
                return pageName;
            }
        }
        return '';
    }

    async getLink(pageName) {
        const nextPageState = pageStates.find(pageState => pageState.name === pageName);
        const link = nextPageState ? nextPageState.link : '';
        if (link === ASYNC_FUNC) {
            const trueLink = await nextPageState.asyncLink(this.props);
            return trueLink;
        }
        return typeof link === 'function' ? link(this.props) : link;
    }

    evaluatePredicate(predicate) {
        // If there is no predicate, then we consider it always true.
        if (!predicate) return true;
        if (Array.isArray(predicate)) {
            // If the give us an empty list we consider it true, even though
            // that is bad form. To avoid a crash we handle it by
            // passing true to the reducer as the initial state.
            return predicate.map(x => x(this.props)).reduce((acc, b) => acc && b, true);
        }
        return predicate(this.props);
    }

    browseToNextPage(link) {
        const { trigger } = this.context;
        if (link === '') { return; }
        const PROTOCOL = 'http';
        window.dispatchEvent(new Event('pageflow.next'));
        if (link.indexOf(PROTOCOL) === -1) {
            const {
                history, rootPath, locale
            } = this.props;
            const newPath = `/${locale.display}/${rootPath}/${link}`;
            history.push(newPath);
        } else {
            const isInternalPage = () => link.includes('/list/estimate-results');
            const { query } = this.props || {};
            const targetUrl = `${link}${collapseQueryParams(query, link, null)}`;
            if (!isInternalPage) {
                trigger('genericEvent', {
                    category: 'handoff-target', action: 'handoff-transition', label: link
                });
            }
            window.location.assign(targetUrl);
        }
    }

    async nextPageLink() {
        const {
            getNextPageName,
            getLink
        } = this;
        const nextPageName = getNextPageName();
        // eslint-disable-next-line no-return-await
        return await getLink(nextPageName);
    }

    async next(pageToUse = '') {
        const {
            browseToNextPage
        } = this;
        if (pageToUse) {
            this.setDisplayNameToUse(pageToUse);
        }
        const nextPageLink = await this.nextPageLink();
        this.setDisplayNameToUse('');
        browseToNextPage(nextPageLink);
    }

    render() {
        const {
            next, nextPageLink, browseToNextPage, props
        } = this;
        const pageFlow = { next, nextPageLink, browseToNextPage };
        return <WrappedComponent {...props} pageFlow={pageFlow} />;
    }
});

/* istanbul ignore next */
const mapDispatchToProps = dispatch => ({
    setDestination: destination => dispatch(AppActions.setDestination(destination))
});

/* istanbul ignore next */
const mapStateToProps = (state) => {
    const {
        app, lead, location, classification, google
    } = state;
    const {
        addressValidation,
        latLng,
        geocoderValidation,
        unitNumberValidation,
        address,
        serverAddressValidation,
        geocoderLatLng,
        partialMatch
    } = location;
    const {
        missingInfo, joinId, autoAuthUrl, eligibilityStatus, propertyId, isPaperContractUser
    } = lead;
    const { hasPhoneNumber, hasPasswordSet } = missingInfo || {};
    const experimentUtil = new ExperimentUtil(app.experiments);
    const isCasDeprecated = true;
    const isGeoCoderEnabled = isGeocoderEnabled(experimentUtil);
    const unitCheckEnabled = isUnitCheckEnabled(experimentUtil);
    const isAddressV2 = isClAddressImprovements(experimentUtil);

    const { placeId } = google;
    const langId = langIdOnLocale(app.locale.canonical);

    return {
        isPaperContractUser,
        serverAddressValidation,
        isAddressV2,
        langId,
        placeId,
        isGeoCoderEnabled,
        geocoderValidation,
        rootPath: app.rootPath,
        locale: app.locale,
        query: app.query,
        experiments: app.experiments,
        createdPropertyTriad: app.createdPropertyTriad,
        expediaId: app.expediaId,
        isLoggedIn: app.isLoggedIn,
        loggedInUserType: app.loggedInUserType,
        hasAdvertiserForSite: app.hasAdvertiserForSite,
        hasPasswordSet,
        hasPhoneNumber,
        addressValidation,
        siteName: app.siteName,
        features: app.features,
        joinId,
        classification,
        latLng,
        autoAuthUrl,
        isCasDeprecated,
        unitNumberValidation,
        unitCheckEnabled,
        eligibilityStatus,
        address,
        pathSource: app.pathSource,
        geocoderLatLng,
        partialMatch,
        userCountryCode: app.userCountryCode,
        propertyId
    };
};

export default compose(connect(mapStateToProps, mapDispatchToProps), PageFlow);
